/**
 * The industries a business can say it works in, and what each one
 * SUGGESTS when a trade opens its first page.
 *
 * A template is a suggestion and nothing more. It proposes services, a
 * short introduction and the intake questions the trade usually asks, so
 * an owner is not staring at an empty page; it is applied only when the
 * owner asks for it, and applying it never edits or removes anything
 * that already exists. An owner who has spent a year naming their
 * services keeps every one of them.
 *
 * WHAT DEPENDS ON THE INDUSTRY. The trade chosen here drives the
 * suggested services, the suggested headline and the suggested INTAKE
 * QUESTIONS — a massage business is offered "Areas of concern" and
 * "Conditions we should know about" as client profile fields, a
 * photographer is offered "Shoot location". Suggestions are added to the
 * existing client profile fields on request; nothing is industry-locked,
 * so a business can keep every question it wrote itself.
 *
 * ADDING A TRADE HERE IS A CODE CHANGE AND NOTHING ELSE. The key is
 * stored as text in `business_industries.industry` (0033 constrains only
 * its shape, never its value), so a new entry needs no migration. Never
 * rename or remove an existing KEY — businesses have it stored — though
 * a name shown to a person can be reworded freely.
 *
 * 'other' is a real answer, not a "prefer not to say": the owner must
 * still tell us what they call their trade, and their own words are kept
 * as the label, so a trade this list has never heard of is describable.
 */

/** The kind of answer an intake question takes; mirrors customer_profile_fields.field_type. */
export type IntakeFieldType = 'text' | 'number' | 'date' | 'choice' | 'boolean';

export interface IntakeQuestion {
  label: string;
  field_type: IntakeFieldType;
  /** Required for 'choice', ignored otherwise. */
  options?: string[];
}

export interface IndustryTemplate {
  /** Catalogue key, stored in business_industries.industry. Never reuse or rename. */
  key: string;
  /** What the owner picks from a list. */
  name: string;
  /** The heading this trade is listed under, so a long list stays readable. */
  group: string;
  /** A first headline, offered for the page and never forced onto it. */
  headline: string;
  /** Services proposed for a brand-new page: name and typical minutes. */
  services: { name: string; duration_minutes: number }[];
  /** The intake questions this trade usually asks, offered as client profile fields. */
  intake: IntakeQuestion[];
  /**
   * True for a trade that SELLS OR RENTS THINGS rather than time.
   *
   * A commerce trade is listed in its own section of the business type
   * picker — see ORDERING_GROUP — because it is not a variation on
   * "choose the service you book"; it turns on a different screen, a
   * different public page and a separate $15.00 activity. Everything
   * else about it is an ordinary trade: the key is stored in
   * `business_industries.industry` exactly as a service trade's is, and
   * a business may hold both.
   */
  commerce?: true;
  /** Starting categories a commerce trade is offered; never forced. */
  categories?: string[];
  /**
   * True for a trade whose customers mostly ASK FOR SOMETHING rather
   * than book a time — arranging a service with a provider, handling
   * paperwork, a consultation that starts with a request. Choosing it
   * gives the business a published service request intake (0053) so
   * the public page takes requests from the first day; appointments
   * still work beside it.
   */
  serviceRequests?: true;
  /**
   * The kinds of request a business in this trade usually takes — the
   * STARTING POINT its default service request intake offers as choices
   * under "What do you need help with?", with "Something else" always
   * last. The owner edits or removes them in the form builder.
   */
  requestStarters?: string[];
  /**
   * The starter intake (`src/shared/form-templates.ts`) a service-request
   * trade copies and publishes when it is chosen. Unset means the generic
   * `service_request_intake`; a trade whose customers ask for one kind of
   * thing — arranging Internet, utilities or an enrollment — names a more
   * specific one. Read through `serviceRequestIntakeFor`.
   */
  intakeTemplate?: string;
  /**
   * True for a commerce trade whose customers mostly ORDER rather than
   * book: prepared food, a food truck. The public page then leads with
   * "Order now" and the shop offers collection as soon as it is ready;
   * appointments still work beneath it. Nothing else changes — the shop
   * is the same 0040 shop.
   */
  orderFirst?: true;
  /**
   * A key kept only so a business that stored it still resolves. Not
   * offered in the picker: `industriesByGroup()` leaves it out. Used when
   * two entries turned out to describe one trade and were merged.
   */
  hidden?: true;
  /**
   * Extra words a person might type into the picker's search to find
   * this trade — "carpet", "water damage", "mold". Search only: never
   * shown, never stored, and never a reason to add a second entry for
   * the same trade. Read through `industryMatchesSearch`.
   */
  keywords?: string[];
}

/**
 * A GROUP IS A HEADING, NOTHING MORE (4.1.92). It organises the picker
 * and names a default picture; it is never stored. A business stores a
 * trade's KEY in `business_industries.industry`, so renaming a group or
 * moving a trade into another one changes what a person reads and
 * nothing a business has saved. 4.1.92 renamed "Home, auto and trades"
 * to "Property & Trade Services" — groups describe WHAT the work is, not
 * whether the customer is residential or commercial — and moved
 * 'auto-services' under its own "Automotive Services" heading, with
 * every key unchanged.
 */
export const PROPERTY_GROUP = 'Property & Trade Services';
export const AUTOMOTIVE_GROUP = 'Automotive Services';

/** The order the groups are shown in. */
export const INDUSTRY_GROUPS = [
  'Hair and barbering',
  'Beauty and skin',
  'Health and wellness',
  'Fitness and training',
  'Pets',
  PROPERTY_GROUP,
  AUTOMOTIVE_GROUP,
  'Professional services',
  'Events and creative',
  'Teaching and lessons',
  'Something else',
] as const;

const YES_NO: IntakeFieldType = 'boolean';

/**
 * What a consulting or request-based business is usually asked for; the
 * customer picks one and adds details. GENERIC ON PURPOSE: the Internet,
 * utility and enrollment work that used to head this list belongs to
 * 'service-setup' below, which has its own intake.
 */
const CONSULTING_REQUEST_STARTERS = [
  'Business consulting or advice',
  'Paperwork, forms and applications',
  'Professional services',
  'Help with an existing matter',
];

/** What a service setup and enrollment business arranges; the customer picks the service they need. */
const SERVICE_SETUP_STARTERS = [
  'Internet',
  'TV or streaming',
  'Phone or mobile',
  'Utilities — electric, gas or water',
  'Home security',
  'A new account',
  'Enrollment or an application',
];

/**
 * What kind of property the work is at. Asked instead of assuming a
 * house: a restoration company works in offices, shops, apartment
 * blocks and warehouses as often as in homes.
 */
export const PROPERTY_TYPES = ['Residential', 'Commercial', 'Multi-unit property', 'Industrial', 'Other'] as const;

/** What a cleaning, restoration and remediation company is usually asked for. */
const RESTORATION_REQUEST_STARTERS = [
  'Carpet or upholstery cleaning',
  'Water damage or flooding',
  'Mold or moisture problem',
  'Fire or smoke damage',
  'Odor problem',
  'Commercial cleanup',
  'Inspection or estimate',
];

const SERVICE_INDUSTRIES: IndustryTemplate[] = [
  {
    key: 'barbering', name: 'Barbering', group: 'Hair and barbering',
    headline: 'Sharp cuts, on your schedule.',
    services: [{ name: 'Haircut', duration_minutes: 30 }, { name: 'Beard trim', duration_minutes: 20 }, { name: 'Cut and beard', duration_minutes: 45 }],
    intake: [{ label: 'Preferred clipper guard', field_type: 'text' }, { label: 'Usual style', field_type: 'text' }],
  },
  {
    key: 'hair-salon', name: 'Hair salon', group: 'Hair and barbering',
    headline: 'Colour, cuts and care.',
    services: [{ name: 'Cut and style', duration_minutes: 60 }, { name: 'Colour', duration_minutes: 120 }, { name: 'Blow dry', duration_minutes: 30 }],
    intake: [{ label: 'Colour formula', field_type: 'text' }, { label: 'Allergies', field_type: 'text' }, { label: 'Chemical straightening in the last year', field_type: YES_NO }],
  },
  {
    key: 'braiding-locs', name: 'Braiding and locs', group: 'Hair and barbering',
    headline: 'Protective styles, done with care.',
    services: [{ name: 'Consultation', duration_minutes: 20 }, { name: 'Box braids', duration_minutes: 240 }, { name: 'Loc retwist', duration_minutes: 90 }],
    intake: [{ label: 'Hair length and texture', field_type: 'text' }, { label: 'Bringing own hair', field_type: YES_NO }, { label: 'Tension sensitivity', field_type: 'text' }],
  },
  {
    key: 'barber-shop-nails', name: 'Nails', group: 'Beauty and skin',
    headline: 'Hands and feet, beautifully done.',
    services: [{ name: 'Manicure', duration_minutes: 45 }, { name: 'Pedicure', duration_minutes: 60 }, { name: 'Gel removal', duration_minutes: 20 }],
    intake: [{ label: 'Preferred shape', field_type: 'text' }, { label: 'Allergies', field_type: 'text' }, { label: 'Diabetes or circulation concerns', field_type: YES_NO }],
  },
  {
    key: 'skin-care', name: 'Skin care and facials', group: 'Beauty and skin',
    headline: 'Skin that feels looked after.',
    services: [{ name: 'Signature facial', duration_minutes: 60 }, { name: 'Consultation', duration_minutes: 30 }, { name: 'Chemical peel', duration_minutes: 45 }],
    intake: [{ label: 'Skin concerns', field_type: 'text' }, { label: 'Products currently used', field_type: 'text' }, { label: 'Retinoid or acid use in the last week', field_type: YES_NO }, { label: 'Known allergies', field_type: 'text' }],
  },
  {
    key: 'lashes-brows', name: 'Lashes and brows', group: 'Beauty and skin',
    headline: 'Eyes open, effort down.',
    services: [{ name: 'Lash extensions — full set', duration_minutes: 120 }, { name: 'Lash fill', duration_minutes: 60 }, { name: 'Brow shaping', duration_minutes: 30 }],
    intake: [{ label: 'Adhesive sensitivity', field_type: YES_NO }, { label: 'Contact lenses', field_type: YES_NO }, { label: 'Desired look', field_type: 'text' }],
  },
  {
    key: 'waxing', name: 'Waxing and hair removal', group: 'Beauty and skin',
    headline: 'Quick, careful, comfortable.',
    services: [{ name: 'Brow wax', duration_minutes: 15 }, { name: 'Leg wax', duration_minutes: 45 }, { name: 'Full body', duration_minutes: 90 }],
    intake: [{ label: 'Medications affecting the skin', field_type: 'text' }, { label: 'Areas to avoid', field_type: 'text' }, { label: 'Last appointment elsewhere', field_type: 'date' }],
  },
  {
    key: 'makeup', name: 'Makeup artistry', group: 'Beauty and skin',
    headline: 'Ready for the photographs.',
    services: [{ name: 'Event makeup', duration_minutes: 60 }, { name: 'Bridal trial', duration_minutes: 90 }, { name: 'Lesson', duration_minutes: 60 }],
    intake: [{ label: 'Occasion', field_type: 'text' }, { label: 'Products to avoid', field_type: 'text' }, { label: 'Getting-ready address', field_type: 'text' }],
  },
  {
    key: 'tattoo-piercing', name: 'Tattoo and piercing', group: 'Beauty and skin',
    headline: 'Booked in, drawn up, done right.',
    services: [{ name: 'Consultation', duration_minutes: 30 }, { name: 'Small tattoo', duration_minutes: 60 }, { name: 'Half day session', duration_minutes: 240 }],
    intake: [{ label: 'Placement and size', field_type: 'text' }, { label: 'Reference notes', field_type: 'text' }, { label: 'Over 18 — identification checked', field_type: YES_NO }, { label: 'Blood thinners or skin conditions', field_type: 'text' }],
  },
  {
    key: 'massage', name: 'Massage and bodywork', group: 'Health and wellness',
    headline: 'Time set aside to feel better.',
    services: [{ name: '60 minute massage', duration_minutes: 60 }, { name: '90 minute massage', duration_minutes: 90 }, { name: 'First visit and intake', duration_minutes: 75 }],
    intake: [
      { label: 'Areas of concern', field_type: 'text' },
      { label: 'Pressure preference', field_type: 'choice', options: ['Light', 'Medium', 'Firm', 'Deep'] },
      { label: 'Injuries or surgeries', field_type: 'text' },
      { label: 'Conditions we should know about', field_type: 'text' },
      { label: 'Pregnant', field_type: YES_NO },
      { label: 'Intake form signed', field_type: YES_NO },
    ],
  },
  {
    key: 'spa-wellness', name: 'Spa and wellness', group: 'Health and wellness',
    headline: 'An hour that belongs to you.',
    services: [{ name: 'Spa day package', duration_minutes: 180 }, { name: 'Body treatment', duration_minutes: 60 }, { name: 'Sauna session', duration_minutes: 45 }],
    intake: [{ label: 'Health conditions', field_type: 'text' }, { label: 'Allergies', field_type: 'text' }, { label: 'Preferences and requests', field_type: 'text' }],
  },
  {
    key: 'chiropractic', name: 'Chiropractic and physical therapy', group: 'Health and wellness',
    headline: 'Move the way you used to.',
    services: [{ name: 'New patient assessment', duration_minutes: 45 }, { name: 'Adjustment', duration_minutes: 20 }, { name: 'Therapy session', duration_minutes: 45 }],
    intake: [{ label: 'Reason for visit', field_type: 'text' }, { label: 'Pain level today (0-10)', field_type: 'number' }, { label: 'Imaging or referral on file', field_type: YES_NO }, { label: 'Current medications', field_type: 'text' }],
  },
  {
    key: 'counseling', name: 'Counselling and coaching', group: 'Health and wellness',
    headline: 'A quiet hour to think it through.',
    services: [{ name: 'Intake session', duration_minutes: 60 }, { name: 'Follow-up session', duration_minutes: 50 }],
    intake: [{ label: 'What you would like to work on', field_type: 'text' }, { label: 'Preferred session format', field_type: 'choice', options: ['In person', 'Video', 'Phone'] }, { label: 'Emergency contact', field_type: 'text' }],
  },
  {
    key: 'nutrition', name: 'Nutrition and dietetics', group: 'Health and wellness',
    headline: 'Eating that fits your life.',
    services: [{ name: 'Initial consultation', duration_minutes: 60 }, { name: 'Review session', duration_minutes: 30 }],
    intake: [{ label: 'Goals', field_type: 'text' }, { label: 'Allergies and intolerances', field_type: 'text' }, { label: 'Conditions we should know about', field_type: 'text' }],
  },
  {
    key: 'fitness', name: 'Personal training', group: 'Fitness and training',
    headline: 'Training that fits your week.',
    services: [{ name: 'Personal session', duration_minutes: 60 }, { name: 'Assessment', duration_minutes: 45 }],
    intake: [{ label: 'Goals', field_type: 'text' }, { label: 'Injuries to work around', field_type: 'text' }, { label: 'Cleared for exercise by a doctor', field_type: YES_NO }],
  },
  {
    key: 'yoga-pilates', name: 'Yoga, pilates and studio classes', group: 'Fitness and training',
    headline: 'Breathe out. Book in.',
    services: [{ name: 'Group class', duration_minutes: 60 }, { name: 'Private session', duration_minutes: 60 }, { name: 'Intro class', duration_minutes: 45 }],
    intake: [{ label: 'Experience level', field_type: 'choice', options: ['New', 'Some experience', 'Experienced'] }, { label: 'Injuries or limitations', field_type: 'text' }, { label: 'Waiver signed', field_type: YES_NO }],
  },
  {
    key: 'sports-coaching', name: 'Sports coaching', group: 'Fitness and training',
    headline: 'Practice with a plan.',
    services: [{ name: 'Private lesson', duration_minutes: 60 }, { name: 'Small group session', duration_minutes: 90 }],
    intake: [{ label: 'Sport and position', field_type: 'text' }, { label: 'Age group', field_type: 'text' }, { label: 'Equipment provided', field_type: YES_NO }],
  },
  {
    key: 'pet-grooming', name: 'Pet grooming', group: 'Pets',
    headline: 'They leave looking brilliant.',
    services: [{ name: 'Bath and brush', duration_minutes: 60 }, { name: 'Full groom', duration_minutes: 120 }, { name: 'Nail trim', duration_minutes: 20 }],
    intake: [{ label: 'Pet name and breed', field_type: 'text' }, { label: 'Weight', field_type: 'number' }, { label: 'Vaccinations up to date', field_type: YES_NO }, { label: 'Behaviour notes', field_type: 'text' }],
  },
  {
    key: 'pet-services', name: 'Pet training, sitting and walking', group: 'Pets',
    headline: 'Looked after while you are out.',
    services: [{ name: 'Meet and greet', duration_minutes: 30 }, { name: 'Training session', duration_minutes: 60 }, { name: 'Walk', duration_minutes: 45 }],
    intake: [{ label: 'Pet name and breed', field_type: 'text' }, { label: 'Feeding and medication notes', field_type: 'text' }, { label: 'Vet contact', field_type: 'text' }],
  },
  {
    key: 'veterinary', name: 'Veterinary', group: 'Pets',
    headline: 'Care when they need it.',
    services: [{ name: 'Wellness exam', duration_minutes: 30 }, { name: 'Vaccination visit', duration_minutes: 20 }, { name: 'Follow-up', duration_minutes: 20 }],
    intake: [{ label: 'Pet name, species and breed', field_type: 'text' }, { label: 'Reason for visit', field_type: 'text' }, { label: 'Current medications', field_type: 'text' }],
  },
  {
    key: 'trades', name: 'Home and trade services', group: PROPERTY_GROUP,
    headline: 'Booked in, turned up, sorted.',
    services: [{ name: 'Callout and quote', duration_minutes: 45 }, { name: 'Standard visit', duration_minutes: 90 }],
    intake: [{ label: 'Address notes', field_type: 'text' }, { label: 'Parking and access', field_type: 'text' }, { label: 'Pets on site', field_type: YES_NO }],
  },
  {
    key: 'plumbing', name: 'Plumbing', group: PROPERTY_GROUP,
    headline: 'Leaks, installs and emergencies.',
    services: [{ name: 'Callout and diagnosis', duration_minutes: 60 }, { name: 'Repair visit', duration_minutes: 90 }, { name: 'Installation', duration_minutes: 180 }, { name: 'Estimate', duration_minutes: 30 }],
    intake: [{ label: 'Problem and where it is', field_type: 'text' }, { label: 'Water shut-off location', field_type: 'text' }, { label: 'Property type', field_type: 'choice', options: ['House', 'Apartment', 'Commercial'] }, { label: 'Parking and access', field_type: 'text' }],
  },
  {
    key: 'electrical', name: 'Electrical', group: PROPERTY_GROUP,
    headline: 'Wired properly, inspected and safe.',
    services: [{ name: 'Callout and diagnosis', duration_minutes: 60 }, { name: 'Repair visit', duration_minutes: 90 }, { name: 'Installation', duration_minutes: 180 }, { name: 'Estimate', duration_minutes: 30 }],
    intake: [{ label: 'Problem and where it is', field_type: 'text' }, { label: 'Panel location and amperage', field_type: 'text' }, { label: 'Permit required', field_type: 'boolean' }, { label: 'Parking and access', field_type: 'text' }],
  },
  {
    key: 'hvac', name: 'Heating, cooling and appliances', group: PROPERTY_GROUP,
    headline: 'Comfortable again, quickly.',
    services: [{ name: 'Diagnostic visit', duration_minutes: 60 }, { name: 'Seasonal service', duration_minutes: 90 }, { name: 'Installation', duration_minutes: 240 }],
    intake: [{ label: 'Equipment make and model', field_type: 'text' }, { label: 'Age of the system', field_type: 'text' }, { label: 'Symptoms', field_type: 'text' }],
  },
  {
    key: 'handyman', name: 'Handyman and repairs', group: PROPERTY_GROUP,
    headline: 'The list, finally finished.',
    services: [{ name: 'Walkthrough and quote', duration_minutes: 30 }, { name: 'Hourly visit', duration_minutes: 60 }, { name: 'Half day', duration_minutes: 240 }],
    intake: [{ label: 'Jobs on the list', field_type: 'text' }, { label: 'Materials supplied by', field_type: 'choice', options: ['Us', 'Customer'] }, { label: 'Parking and access', field_type: 'text' }],
  },
  {
    key: 'landscaping', name: 'Landscaping and outdoor', group: PROPERTY_GROUP,
    headline: 'Outside, looked after.',
    services: [{ name: 'Estimate visit', duration_minutes: 30 }, { name: 'Maintenance visit', duration_minutes: 90 }, { name: 'Seasonal clean-up', duration_minutes: 240 }],
    intake: [{ label: 'Property size', field_type: 'text' }, { label: 'Gate code or access', field_type: 'text' }, { label: 'Pets in the yard', field_type: 'boolean' }],
  },
  {
    key: 'cleaning', name: 'Cleaning services', group: PROPERTY_GROUP,
    headline: 'Come home to done.',
    services: [{ name: 'Standard clean', duration_minutes: 120 }, { name: 'Deep clean', duration_minutes: 240 }, { name: 'Walkthrough and quote', duration_minutes: 30 }],
    intake: [{ label: 'Bedrooms and bathrooms', field_type: 'text' }, { label: 'Entry instructions', field_type: 'text' }, { label: 'Products to avoid', field_type: 'text' }, { label: 'Pets on site', field_type: YES_NO }],
  },
  {
    // ONE TRADE FOR CLEANING, RESTORATION AND REMEDIATION (4.1.92), whether
    // the company only cleans carpets or also dries out a flooded
    // warehouse: the owner switches on the services it actually provides.
    // NOT split into residential and commercial — the intake asks the
    // property type instead — and NOT one entry per service; the words a
    // person searches for are `keywords`. Routine work (carpet,
    // upholstery, tile, inspections, estimates) is booked as an ordinary
    // appointment; restoration, whose length nobody knows yet, arrives
    // through the service-request workflow (0053) with its own intake,
    // `restoration_request_intake`. It can sit beside 'cleaning' in the
    // same business, at no extra charge.
    key: 'cleaning-restoration-remediation', name: 'Cleaning, Restoration & Remediation', group: PROPERTY_GROUP,
    headline: 'Cleaned, restored, and ready again.',
    services: [
      { name: 'Carpet cleaning', duration_minutes: 120 },
      { name: 'Upholstery cleaning', duration_minutes: 90 },
      { name: 'Tile and grout cleaning', duration_minutes: 120 },
      { name: 'Area rug cleaning', duration_minutes: 60 },
      { name: 'Commercial carpet cleaning', duration_minutes: 180 },
      { name: 'Water extraction', duration_minutes: 180 },
      { name: 'Water damage restoration', duration_minutes: 240 },
      { name: 'Flood cleanup', duration_minutes: 240 },
      { name: 'Mold remediation', duration_minutes: 240 },
      { name: 'Fire and smoke cleanup', duration_minutes: 240 },
      { name: 'Odor removal', duration_minutes: 120 },
      { name: 'Emergency restoration service', duration_minutes: 180 },
      { name: 'Commercial cleanup', duration_minutes: 240 },
      { name: 'Inspection and estimate', duration_minutes: 60 },
    ],
    intake: [
      { label: 'Property type', field_type: 'choice', options: [...PROPERTY_TYPES] },
      { label: 'Approximate affected area', field_type: 'text' },
      { label: 'Access instructions', field_type: 'text' },
      { label: 'Insurance company', field_type: 'text' },
      { label: 'Insurance claim number', field_type: 'text' },
    ],
    serviceRequests: true,
    intakeTemplate: 'restoration_request_intake',
    requestStarters: RESTORATION_REQUEST_STARTERS,
    keywords: [
      'carpet', 'carpet cleaning', 'upholstery', 'tile cleaning', 'tile and grout', 'rug cleaning',
      'restoration', 'water damage', 'water restoration', 'water extraction', 'flood', 'flooding',
      'mold', 'mould', 'moisture', 'remediation', 'fire damage', 'smoke', 'odor', 'odour',
      'commercial cleaning', 'emergency cleanup', 'emergency cleaning', 'property restoration', 'disaster cleanup',
    ],
  },
  {
    key: 'auto-services', name: 'Auto repair and detailing', group: AUTOMOTIVE_GROUP,
    headline: 'Booked in and back on the road.',
    services: [{ name: 'Diagnostic', duration_minutes: 60 }, { name: 'Full detail', duration_minutes: 180 }, { name: 'Standard service', duration_minutes: 90 }],
    intake: [{ label: 'Vehicle year, make and model', field_type: 'text' }, { label: 'Mileage', field_type: 'number' }, { label: 'Concern reported', field_type: 'text' }],
  },
  {
    // 4.1.73 SPLIT THE OLD 'professional-services' ("Accounting, legal and
    // notary") INTO TWO. The old key is kept hidden below so a business
    // that stored it still resolves to the same name.
    key: 'accounting-tax', name: 'Accounting, bookkeeping & tax', group: 'Professional services',
    headline: 'Your numbers, in order.',
    services: [{ name: 'Initial consultation', duration_minutes: 45 }, { name: 'Tax preparation appointment', duration_minutes: 60 }, { name: 'Bookkeeping review', duration_minutes: 60 }],
    intake: [{ label: 'Matter or reason for the appointment', field_type: 'text' }, { label: 'Documents to bring', field_type: 'text' }, { label: 'Personal or business', field_type: 'choice', options: ['Personal', 'Business', 'Both'] }],
  },
  {
    key: 'legal-notary', name: 'Legal & notary', group: 'Professional services',
    headline: 'An appointment, not a phone queue.',
    services: [{ name: 'Initial consultation', duration_minutes: 45 }, { name: 'Document signing', duration_minutes: 30 }, { name: 'Notary appointment', duration_minutes: 20 }],
    intake: [{ label: 'Matter or reason for the appointment', field_type: 'text' }, { label: 'Documents to bring', field_type: 'text' }, { label: 'Identification verified', field_type: YES_NO }],
  },
  {
    key: 'real-estate', name: 'Real estate & property', group: 'Professional services',
    headline: 'Viewings that fit your day.',
    services: [{ name: 'Viewing', duration_minutes: 30 }, { name: 'Valuation', duration_minutes: 60 }, { name: 'Consultation', duration_minutes: 45 }],
    intake: [{ label: 'Property of interest', field_type: 'text' }, { label: 'Budget range', field_type: 'text' }, { label: 'Pre-approved for finance', field_type: YES_NO }],
  },
  {
    // A business that ARRANGES a service for a customer — Internet, TV,
    // phone, utilities, home security, a new account, an enrollment or an
    // application. Nothing about that has a start time, so the trade
    // brings the service-request workflow (0053) with its own intake
    // (`service_setup_intake`): what they need, where, by when, whether
    // they are moving in, a preferred provider, how to reach them. The
    // team records the same request after a phone call or a walk-in.
    key: 'service-setup', name: 'Service setup & enrollment', group: 'Professional services',
    headline: 'Tell us what you need set up. We arrange it.',
    services: [{ name: 'Phone consultation', duration_minutes: 20 }, { name: 'In-person setup appointment', duration_minutes: 45 }],
    intake: [{ label: 'Current provider', field_type: 'text' }, { label: 'Account or reference number', field_type: 'text' }],
    serviceRequests: true,
    intakeTemplate: 'service_setup_intake',
    requestStarters: SERVICE_SETUP_STARTERS,
  },
  {
    // ONE ENTRY FOR CONSULTING, whether the work is booked as an hour or
    // asked for as a request (0053): a consultant takes appointments AND
    // service requests, so this trade does both, with the GENERIC intake.
    // 'service-requests' below is the key 4.1.63 briefly offered as a
    // second entry; it is kept hidden so the businesses that chose it
    // still resolve.
    key: 'business-consulting', name: 'Consulting & service requests', group: 'Professional services',
    headline: 'Tell us what you need. We take it from there.',
    services: [{ name: 'Discovery call', duration_minutes: 30 }, { name: 'Strategy session', duration_minutes: 90 }],
    intake: [{ label: 'Company and role', field_type: 'text' }, { label: 'What you want to solve', field_type: 'text' }],
    serviceRequests: true,
    requestStarters: CONSULTING_REQUEST_STARTERS,
  },
  {
    // Retired in 4.1.73, split into 'accounting-tax' and 'legal-notary'.
    // Never offered, never removed: a business that stored it keeps it.
    key: 'professional-services', name: 'Accounting, legal and notary', group: 'Professional services', hidden: true,
    headline: 'An appointment, not a phone queue.',
    services: [{ name: 'Initial consultation', duration_minutes: 45 }, { name: 'Document signing', duration_minutes: 30 }, { name: 'Annual review', duration_minutes: 60 }],
    intake: [{ label: 'Matter or reason for the appointment', field_type: 'text' }, { label: 'Documents to bring', field_type: 'text' }, { label: 'Identification verified', field_type: YES_NO }],
  },
  {
    // Retired alias of 'business-consulting' (see above). Never offered,
    // never removed.
    key: 'service-requests', name: 'Consulting & service requests', group: 'Professional services', hidden: true,
    headline: 'Tell us what you need. We take it from there.',
    services: [{ name: 'Consultation', duration_minutes: 30 }],
    intake: [],
    serviceRequests: true,
    requestStarters: CONSULTING_REQUEST_STARTERS,
  },
  {
    key: 'photography', name: 'Photography', group: 'Events and creative',
    headline: 'Portraits, events and everything in between.',
    services: [{ name: 'Portrait session', duration_minutes: 60 }, { name: 'Family session', duration_minutes: 90 }, { name: 'Headshots', duration_minutes: 45 }],
    intake: [{ label: 'Shoot location', field_type: 'text' }, { label: 'Usage rights discussed', field_type: YES_NO }, { label: 'People in the shoot', field_type: 'number' }],
  },
  {
    key: 'videography', name: 'Video and audio production', group: 'Events and creative',
    headline: 'Filmed properly, delivered on time.',
    services: [{ name: 'Consultation', duration_minutes: 30 }, { name: 'Half day shoot', duration_minutes: 240 }, { name: 'Studio session', duration_minutes: 120 }],
    intake: [{ label: 'Project and deliverables', field_type: 'text' }, { label: 'Location and power access', field_type: 'text' }, { label: 'Deadline', field_type: 'date' }],
  },
  {
    key: 'events-catering', name: 'Event planning & coordination', group: 'Events and creative',
    headline: 'Your day, run to time.',
    services: [{ name: 'Planning consultation', duration_minutes: 60 }, { name: 'Tasting', duration_minutes: 90 }, { name: 'Venue walkthrough', duration_minutes: 60 }],
    intake: [{ label: 'Event date and venue', field_type: 'text' }, { label: 'Guest count', field_type: 'number' }, { label: 'Dietary requirements', field_type: 'text' }],
  },
  {
    key: 'tutoring', name: 'Tutoring and academic lessons', group: 'Teaching and lessons',
    headline: 'One-to-one, at your pace.',
    services: [{ name: 'Single lesson', duration_minutes: 60 }, { name: 'Trial lesson', duration_minutes: 30 }],
    intake: [{ label: 'Subject and level', field_type: 'text' }, { label: 'Exam date', field_type: 'date' }, { label: 'Areas to focus on', field_type: 'text' }],
  },
  {
    key: 'music-lessons', name: 'Music and performing arts lessons', group: 'Teaching and lessons',
    headline: 'Play it properly.',
    services: [{ name: 'Trial lesson', duration_minutes: 30 }, { name: 'Weekly lesson', duration_minutes: 45 }],
    intake: [{ label: 'Instrument and level', field_type: 'text' }, { label: 'Has an instrument at home', field_type: YES_NO }, { label: 'Goals', field_type: 'text' }],
  },
  {
    key: 'driving-school', name: 'Driving instruction', group: 'Teaching and lessons',
    headline: 'On the road, when you are ready.',
    services: [{ name: 'Assessment lesson', duration_minutes: 60 }, { name: 'Standard lesson', duration_minutes: 90 }, { name: 'Test preparation', duration_minutes: 120 }],
    intake: [{ label: 'Licence or permit number', field_type: 'text' }, { label: 'Hours already driven', field_type: 'number' }, { label: 'Pick-up address', field_type: 'text' }],
  },
  {
    key: 'other', name: 'Something else — tell us what you call it', group: 'Something else',
    headline: 'Book a time with us.',
    services: [{ name: 'Appointment', duration_minutes: 60 }],
    intake: [],
  },
];

/**
 * The heading the commerce trades are listed under, and the words the
 * owner sees. It is deliberately not one of INDUSTRY_GROUPS: the picker
 * shows this section SEPARATELY, below the service trades, because
 * choosing one of these turns on ordering — a different public page and
 * a separate monthly activity — rather than just seeding a service list.
 */
export const ORDERING_GROUP = 'Products, Custom Orders, and Rentals';

/**
 * Food and beverage (4.1.73): prepared food, a food truck, catering, a
 * bakery. THEY ARE COMMERCE TRADES — a menu is categories and items, an
 * order is a 0040 order, and collection or delivery is the shop's own
 * fulfilment — so they live in the commerce section beside the products
 * and rentals, under their own heading. There is no restaurant system:
 * no table plan, no delivery routing, no ingredient stock, no kitchen
 * screen beyond the Queue tab the kiosk (0056) already added.
 */
export const FOOD_GROUP = 'Food and beverage';

/** The commerce section's heading over both groups, on a picker that shows them together. */
export const COMMERCE_SECTION = 'Food, products, custom orders and rentals';

/** The commerce groups, in the order they are shown. */
export const COMMERCE_GROUPS = [FOOD_GROUP, ORDERING_GROUP] as const;

/**
 * Trades that sell or rent THINGS.
 *
 * Every one of them shares a single answer: the owner makes their own
 * categories and puts whatever they sell or rent into them — cookies,
 * balloons, a dining table they build to order, thirty chairs, a bouncy
 * castle. So these entries suggest NO services and NO intake questions;
 * what they suggest is a starting set of CATEGORIES, which
 * /ordering offers to create and never forces. The list exists so that
 * an owner can recognise their own trade and say so, not because the
 * product behaves differently for a baker than for a tool hire.
 *
 * 'other-products' is a real answer and demands the owner's own words,
 * exactly as 'other' does for the service trades.
 */
export const ORDERING_INDUSTRIES: IndustryTemplate[] = [
  // Food and beverage. 'prepared-food-orders' and 'baked-goods' are the
  // keys 0040 shipped under the products group, moved here and reworded;
  // a business that stored either keeps it.
  { key: 'prepared-food-orders', name: 'Prepared food & takeout', group: FOOD_GROUP, commerce: true, orderFirst: true,
    headline: 'Order now. Ready when you are.', services: [], intake: [],
    categories: ['Mains', 'Sides and extras', 'Drinks', 'Trays and platters'] },
  { key: 'food-truck', name: 'Food truck, stand & pop up', group: FOOD_GROUP, commerce: true, orderFirst: true,
    headline: 'Order ahead and skip the line.', services: [], intake: [],
    categories: ['Menu', 'Specials', 'Drinks'] },
  { key: 'catering', name: 'Catering', group: FOOD_GROUP, commerce: true,
    headline: 'Feeding the whole room.', services: [], intake: [],
    categories: ['Trays and platters', 'Per-person packages', 'Custom orders'] },
  { key: 'baked-goods', name: 'Baked goods, cakes & treats', group: FOOD_GROUP, commerce: true,
    headline: 'Order something lovely.', services: [], intake: [],
    categories: ['Cookies', 'Cakes', 'Custom orders'] },
  // Products, custom orders and rentals.
  { key: 'balloons-party-decor', name: 'Balloons and party décor', group: ORDERING_GROUP, commerce: true,
    headline: 'Make the room look like a party.', services: [], intake: [],
    categories: ['Balloon garlands', 'Bouquets', 'Custom orders'] },
  { key: 'custom-woodworking', name: 'Custom woodworking and furniture', group: ORDERING_GROUP, commerce: true,
    headline: 'Made to order, made to last.', services: [], intake: [],
    categories: ['Ready to buy', 'Custom builds'] },
  { key: 'custom-apparel', name: 'Custom apparel, printing and signs', group: ORDERING_GROUP, commerce: true,
    headline: 'Your name on it.', services: [], intake: [],
    categories: ['Shirts', 'Signs', 'Custom orders'] },
  { key: 'flowers-and-gifts', name: 'Flowers, gifts and hampers', group: ORDERING_GROUP, commerce: true,
    headline: 'Sent with something to say.', services: [], intake: [],
    categories: ['Bouquets', 'Gift boxes', 'Custom orders'] },
  { key: 'party-equipment-rental', name: 'Party and event rentals — chairs, tables, jumpers', group: ORDERING_GROUP, commerce: true,
    headline: 'Everything the party needs, delivered.', services: [], intake: [],
    categories: ['Seating', 'Tables and linens', 'Jumpers and games'] },
  { key: 'tool-equipment-rental', name: 'Tool and equipment rentals', group: ORDERING_GROUP, commerce: true,
    headline: 'Borrow the right tool.', services: [], intake: [],
    categories: ['Power tools', 'Yard equipment', 'Trailers'] },
  { key: 'handmade-retail', name: 'Handmade and retail goods', group: ORDERING_GROUP, commerce: true,
    headline: 'Made here, sold here.', services: [], intake: [],
    categories: ['In stock', 'Made to order'] },
  { key: 'other-products', name: 'Something else we sell or rent — tell us what you call it', group: ORDERING_GROUP, commerce: true,
    headline: 'Order from us.', services: [], intake: [],
    categories: ['Products', 'Custom orders', 'Rentals'] },
];

/**
 * Every trade the product knows, service and commerce alike, in one map
 * so a stored key resolves however it was chosen.
 */
export const INDUSTRIES: IndustryTemplate[] = [...SERVICE_INDUSTRIES, ...ORDERING_INDUSTRIES];

const BY_KEY = new Map(INDUSTRIES.map((i) => [i.key, i]));

/** The template for a key, or the open-ended one when it is not listed. */
export function industryTemplate(key: string): IndustryTemplate {
  return BY_KEY.get(key) ?? BY_KEY.get('other')!;
}

/**
 * How an industry is named to a person.
 *
 * ONLY "Something else" CARRIES THE OWNER'S OWN WORDS (4.1.86). A
 * predefined trade is Book with Assemble's own, and is always shown by
 * its catalogue name: the picker no longer offers "Call it something
 * else" beside Barber or Photography, and a label an owner typed there
 * before 4.1.86 is ignored rather than deleted (the next save clears
 * it). A key this catalogue does not know is shown as stored.
 */
export function industryLabel(key: string, label?: string | null): string {
  const own = industryOwnWords(key, label);
  if (own) return own;
  const known = BY_KEY.get(key);
  if (!known) return label?.trim() || key;
  return industryNeedsLabel(key) ? shortCustomName(known.name) : known.name;
}

/**
 * The owner's own words, kept only where they mean something: on
 * "Something else". Every writer (signup, Add a business, Businesses →
 * edit, Trades & extra pages, the platform workspace) stores this and
 * nothing else, so a predefined trade never carries a private rename.
 */
export function industryOwnWords(key: string, label?: string | null): string | null {
  if (!industryNeedsLabel(key)) return null;
  return label?.trim() || null;
}

/** True for a trade the owner named themselves, not one from the catalogue. */
export function isCustomIndustry(key: string): boolean {
  return industryNeedsLabel(key);
}

/**
 * The name with its origin spelled out, for screens that must tell a
 * custom trade from an official one — the platform workspace, the
 * picker's chosen list: "Something else: Dog grooming".
 */
export function industryDescription(key: string, label?: string | null): string {
  const own = industryOwnWords(key, label);
  const base = shortCustomName(BY_KEY.get(key)?.name ?? key);
  if (!isCustomIndustry(key)) return industryLabel(key, label);
  return own ? `${base}: ${own}` : `${base} (not named yet)`;
}

/** "Something else — tell us what you call it" → "Something else". */
function shortCustomName(name: string): string {
  return name.replace(/\s+—\s+tell us what you call it$/, '');
}

/** The catalogue in listing order, grouped for a menu or a checklist. */
export function industriesByGroup(): { group: string; items: IndustryTemplate[] }[] {
  return INDUSTRY_GROUPS
    .map((group) => ({ group, items: INDUSTRIES.filter((i) => i.group === group && !i.hidden) }))
    .filter((g) => g.items.length > 0);
}

/**
 * Words that find a whole GROUP from the picker's search, so somebody
 * typing "property services", "trades" or "mechanic" is shown the right
 * heading. Search only, like a trade's own `keywords`.
 */
export const GROUP_KEYWORDS: Record<string, string[]> = {
  [PROPERTY_GROUP]: ['property services', 'property', 'trades', 'trade services', 'home services', 'contractor', 'commercial property', 'maintenance'],
  [AUTOMOTIVE_GROUP]: ['auto', 'car', 'vehicle', 'mechanic', 'garage', 'detailing'],
};

/**
 * True when a picker search matches this trade: its name, key, group,
 * the group's own search words, its keywords, request starters,
 * categories or suggested services. Empty search matches everything.
 * The one statement of it, shared by the owner's and the platform's
 * picker (they are the same component) and by the tests.
 */
export function industryMatchesSearch(i: IndustryTemplate, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [
    i.name, i.key, i.group, ...(GROUP_KEYWORDS[i.group] ?? []), ...(i.keywords ?? []),
    ...(i.requestStarters ?? []), ...(i.categories ?? []), ...i.services.map((x) => x.name),
  ].some((text) => text.toLowerCase().includes(q));
}

/**
 * Shown beside the service-trade picker. Trades are what ONE business
 * does; they are never billed. The second sentence stops the first from
 * being read as "everything is free": people and add-ons still cost what
 * they cost.
 */
export const SAME_BUSINESS_INDUSTRY_NOTE =
  'Choose every type of work your business provides. Adding related service industries to the same business does not increase your monthly price.';
export const SEPARATELY_PRICED_NOTE =
  'Additional professionals, marketing, the shop and text credits are still priced separately.';

/**
 * True when this trade needs the owner's own words before it means
 * anything. 'other' is a real answer, but only once it is named.
 */
export function industryNeedsLabel(key: string): boolean {
  return key === 'other' || key === 'other-products';
}

/**
 * The commerce section of the business type picker, kept apart from the
 * service trades on purpose. `industriesByGroup()` walks INDUSTRY_GROUPS
 * and so never returns these: the two lists are shown as two sections,
 * and a screen that wants both asks for both. Two groups since 4.1.73:
 * food and beverage first, then products, custom orders and rentals.
 */
export function orderingIndustriesByGroup(): { group: string; items: IndustryTemplate[] }[] {
  return COMMERCE_GROUPS
    .map((group) => ({ group, items: ORDERING_INDUSTRIES.filter((i) => i.group === group && !i.hidden) }))
    .filter((g) => g.items.length > 0);
}

/** True when this trade sells or rents things rather than time. */
export function isOrderingIndustry(key: string): boolean {
  return ORDERING_INDUSTRIES.some((i) => i.key === key);
}

/** True when this trade's customers mostly order rather than book, so a page leads with "Order now". */
export function isOrderFirstIndustry(key: string | null | undefined): boolean {
  return !!key && BY_KEY.get(key)?.orderFirst === true;
}

/** True when this trade takes service requests rather than (only) appointments. */
export function isServiceRequestIndustry(key: string): boolean {
  return BY_KEY.get(key)?.serviceRequests === true;
}

/** The request starters for a business in these trades: the first trade that has any. */
export function requestStartersFor(keys: readonly string[]): string[] {
  for (const key of keys) {
    const starters = BY_KEY.get(key)?.requestStarters;
    if (starters?.length) return [...starters];
  }
  return [];
}

/** What a business's default service request intake is built from. */
export interface ServiceRequestIntakeChoice {
  /** The starter template key in `form-templates.ts`. */
  templateKey: string;
  /** The choices its "what do you need" question offers, before "Something else". */
  starters: string[];
}

/** The generic intake every service-request trade falls back to. */
export const DEFAULT_SERVICE_REQUEST_TEMPLATE = 'service_request_intake';

/**
 * The intake for a business in these trades: the FIRST service-request
 * trade in the list decides both the template and the starters, so a
 * business's primary trade (listed first) wins. A business in none of
 * them gets the generic template with no starters, which is what the
 * "Set up the default intake" button offers.
 */
export function serviceRequestIntakeFor(keys: readonly string[]): ServiceRequestIntakeChoice {
  for (const key of keys) {
    const trade = BY_KEY.get(key);
    if (trade?.serviceRequests) {
      return { templateKey: trade.intakeTemplate ?? DEFAULT_SERVICE_REQUEST_TEMPLATE, starters: [...(trade.requestStarters ?? [])] };
    }
  }
  return { templateKey: DEFAULT_SERVICE_REQUEST_TEMPLATE, starters: [] };
}

/** The shape business_industries.industry accepts. */
export const INDUSTRY_KEY_PATTERN = /^[a-z0-9][a-z0-9_-]{0,39}$/;
/** The shape a variant's path segment accepts; mirrors the CHECK in 0033. */
export const PAGE_SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{0,47}$/;

/**
 * A picture of the trade — someone doing the job where we have a
 * photograph, a flat illustration of its tools where we do not.
 *
 * SHOWN SMALL IN THE CONSOLE (the business card in the sidebar, the
 * Businesses screen) AND LARGE ON THE PUBLIC PAGE as the default cover
 * for a business that has not uploaded its own. A business's own cover
 * always wins. The files live in `public/industries/` and
 * `public/home-*.svg`; replacing one with a better picture of the same
 * name changes every business in that trade at once, and adding a
 * trade-specific entry to `TRADE_PICTURES` gives one trade its own.
 *
 * `photo` says whether it is a photograph, so a screen can crop a photo
 * edge to edge and let an illustration sit whole on its background.
 */
export interface IndustryPicture {
  src: string;
  /** Plain words for the alt text. */
  alt: string;
  photo: boolean;
}

const TRADE_PICTURES: Record<string, IndustryPicture> = {
  'photography': { src: '/home-photographer.svg', alt: 'A photographer at work', photo: true },
  'videography': { src: '/home-photographer.svg', alt: 'A photographer at work', photo: true },
  'events-catering': { src: '/home-catering.svg', alt: 'A chef plating a dish', photo: true },
};

const SOMETHING_ELSE: IndustryPicture = { src: '/industries/something-else.svg', alt: 'A shop front', photo: false };

// Keyed by the group's NAME, not its position: a group added to the
// middle of the list must not shift every picture below it.
const GROUP_PICTURES: Record<string, IndustryPicture> = {
  'Hair and barbering': { src: '/home-salon-tools.svg', alt: 'A stylist cutting hair', photo: true },
  'Beauty and skin': { src: '/industries/beauty-and-skin.svg', alt: 'Beauty and skin care tools', photo: false },
  'Health and wellness': { src: '/industries/health-and-wellness.svg', alt: 'A leaf and stones', photo: false },
  'Fitness and training': { src: '/industries/fitness-and-training.svg', alt: 'A barbell', photo: false },
  'Pets': { src: '/industries/pets.svg', alt: 'A paw print and a bowl', photo: false },
  [PROPERTY_GROUP]: { src: '/industries/home-and-trades.svg', alt: 'A building and a wrench', photo: false },
  [AUTOMOTIVE_GROUP]: { src: '/industries/automotive.svg', alt: 'A car', photo: false },
  'Professional services': { src: '/industries/professional-services.svg', alt: 'A document with a tick', photo: false },
  'Events and creative': { src: '/home-photographer.svg', alt: 'A photographer at work', photo: true },
  'Teaching and lessons': { src: '/industries/teaching-and-lessons.svg', alt: 'An open book and a pencil', photo: false },
  'Something else': SOMETHING_ELSE,
  [FOOD_GROUP]: { src: '/home-catering.svg', alt: 'A chef plating a dish', photo: true },
  [ORDERING_GROUP]: { src: '/industries/products-and-rentals.svg', alt: 'A parcel', photo: false },
};

/** The picture for a trade: its own if it has one, else its group's, else the generic shop front. */
export function industryPicture(key: string | null | undefined): IndustryPicture {
  if (!key) return SOMETHING_ELSE;
  return TRADE_PICTURES[key] ?? GROUP_PICTURES[industryTemplate(key).group] ?? SOMETHING_ELSE;
}
